/**
 * Course player unit body.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Box } from "@mui/material";

import { getModuleIdFromCourseId, getNumberOfQuestionsFromXMLString, mapSectionToGradeDetails } from "utils/grade.util";
import { useAsterController } from "context";
import { getSectionTitle, getSequenceTitle, getVerticalTitle } from "utils/course";
import { actionAppCourseUnitGet, actionAnswerSubmit } from "context/action";
import ClassicModuleRender from "./renderer/classic";
import TestPositionmentRender from "./renderer/test_positionment";
import MockExamModuleRender from "./renderer/mock_quiz";
import FinalEvaluationRender from "./renderer/final_evaluation";
import ConclusionRender from "./renderer/conclusion";
import { VText } from "form";
import BoardReview from "./board.review";
import CommentCard from "pages/common/CommentCard";
import CourseCommentItem from "pages/course/course.comment.item";
import BoardPagination from "./board.pagniation";

const MODULE_TYPE = {
  HTML: "html",
  MOCKEXAM: "mockexam",
  MOCKFINAL: "mockfinal",
  PRETEST: "mocktestposition",
  CONCLUSION: "conclusion",
};

/**
 * Dispatches the current vertical to classic, quiz, evaluation, or conclusion renderers.
 */
function BoardContent() {
  const { t } = useTranslation("common");

  const { courseId, sectionIndex, sequenceIndex, verticalIndex } = useParams();
  const location = useLocation();

  const [controller, dispatch] = useAsterController();
  const { user, structure, loadedCourseUnit, xblock, courses, comments, loadedCommentList, preview, sessionHydrated, firstLoadPending } = controller;

  const [xblocks, setXBlocks] = useState([]);
  const [nonGrades, setNonGrades] = useState([]);
  const [moduleType, setModuleType] = useState(MODULE_TYPE.HTML);
  const [stackedAnswers, setStackedAnswers] = useState([]);
  const [discussionId, setDiscussionId] = useState(null);
  const [isPosted, setPosted] = useState(false);

  const currCourse = useMemo(() => (courses.data || []).find((v) => v.id === courseId) || {}, [courses, courseId]);
  const sectionTitle = getSectionTitle(structure, sectionIndex);
  const sequenceTitle = getSequenceTitle(structure, sectionIndex, sequenceIndex);
  const draftKey = `aster-page-draft:${location.pathname}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(draftKey);
      setStackedAnswers(raw ? JSON.parse(raw) : []);
    } catch {
      setStackedAnswers([]);
    }
  }, [draftKey]);

  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(stackedAnswers));
    } catch {
      /* private mode */
    }
  }, [draftKey, stackedAnswers]);

  // Eval whether this user already posted comment
  useEffect(() => loadedCommentList && setPosted((comments.data || []).find((v) => Number(v.author_id) === user.id) ? true : false), [comments, (comments.data || []).length, loadedCommentList, user.id]);

  // Dispatch get xblocks, set itemlist check icon
  useEffect(() => {
    if (!sessionHydrated || firstLoadPending) return;
    if (Object.keys(structure).length && courseId && sectionIndex && sequenceIndex && verticalIndex) {
      // Fetch xblock data from server
      const currVertical = structure.sections[sectionIndex]?.sequences[sequenceIndex]?.verticals[verticalIndex];
      if (!currVertical) return;

      const currXBlocks = currVertical.xblock_ids ?? [];

      let discussionId = null;
      if (currXBlocks.length) {
        discussionId = currXBlocks.find((v) => v.type === "discussion")?.definition_id;
      }
      const vimeoId = currXBlocks.find((v) => v.type === "videoxblock" || v.type === "video")?.vimeo_id;
      const definitionIds = currXBlocks.map((v) => v.definition_id);

      const value = {
        course_id: courseId,
        vertical_id: currVertical.vertical_id,
        discussion_id: discussionId,
        vimeo_id: vimeoId,
        definition_ids: definitionIds,
        router_url: `${sectionIndex}/${sequenceIndex}/${verticalIndex}`,
      };
      // Drop prior unit html so a stale xblock_list cannot briefly wipe SCORM sha → /by-sha//.
      setXBlocks(currXBlocks.map((b) => ({ ...b })));
      actionAppCourseUnitGet(dispatch, value);
      setDiscussionId(discussionId);
    }
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
  }, [sessionHydrated, firstLoadPending, structure, courseId, sectionIndex, sequenceIndex, verticalIndex, dispatch, location.pathname]);

  // After fetching xblocks, map block content according to the structure xblock ids
  useEffect(() => {
    if (Object.keys(structure).length && xblock.length && currCourse) {
      let blocks = structure.sections?.[sectionIndex]?.sequences?.[sequenceIndex]?.verticals?.[verticalIndex]?.xblock_ids || [];

      // Copy so we do not mutate course_structure in the store; attach unit html and
      // rewrite stale VAR_SCORM_HASH placeholders left in cached course outlines.
      blocks = blocks.map((b) => {
        const match = xblock.find((x) => String(x.definition_id) === String(b.definition_id));
        if (!match) return { ...b };
        const html = match.html;
        const sha = String(html || "").trim();
        let raw_html = b.raw_html;
        if (b.type === "scorm" && sha && String(raw_html || "").includes("VAR_SCORM_HASH")) {
          raw_html = String(raw_html).replace(/VAR_SCORM_HASH/g, sha);
        }
        return { ...b, html, raw_html };
      });

      setXBlocks(blocks);

      // Set current vertical type
      const mapper = mapSectionToGradeDetails(currCourse);
      const currentMapper = mapper.find((m) => m["sectionTitle"] === sectionTitle);
      let currModuleGradeFormat = "other";
      if (currentMapper) {
        const gformat = mapper.find((m) => m["sectionTitle"] === sectionTitle)["gradeFormat"];
        currModuleGradeFormat = gformat ? gformat.toLowerCase().trim() : currModuleGradeFormat;
      }

      let currModuleType = MODULE_TYPE.HTML;
      if (blocks.findIndex((b) => b.type === MODULE_TYPE.MOCKEXAM) >= 0) {
        // Evaluate whether this is mock or final
        if (currModuleGradeFormat.toLowerCase() === "quiz") currModuleType = MODULE_TYPE.MOCKEXAM;
        else if (currModuleGradeFormat.toLowerCase() === "test de positionnement") currModuleType = MODULE_TYPE.PRETEST;
        else if (currModuleGradeFormat.toLowerCase() === "évaluation finale" || currModuleGradeFormat.toLocaleLowerCase() === "evaluation finale") currModuleType = MODULE_TYPE.MOCKFINAL;
        else if (currModuleGradeFormat.toLowerCase() === "quiz de satisfaction" || currModuleGradeFormat.toLowerCase() === "questionnaire de satisfaction") currModuleType = MODULE_TYPE.CONCLUSION;
        else currModuleType = MODULE_TYPE.MOCKEXAM;
      } else if (currModuleGradeFormat.toLowerCase() === "quiz de satisfaction") {
        currModuleType = MODULE_TYPE.CONCLUSION;
      }
      setModuleType(currModuleType);
    }
  }, [structure, xblock, currCourse, sectionIndex, sequenceIndex, verticalIndex, sectionTitle]);

  // For problem blocks, find out grades that are not exist and mimic virtual grades
  useEffect(() => {
    if (loadedCourseUnit && xblocks.length && currCourse) {
      let nonGradeTmp = [];
      for (let block of xblocks) {
        if (block.type !== "problem") continue;

        let grade = currCourse.grades?.find((g) => g.module_id.includes(block.id));
        // If there is no grade, then we mimic the grade choice and will send it to API when any submit is clicked
        if (!grade) {
          const questionNumber = getNumberOfQuestionsFromXMLString(block.html);
          nonGradeTmp.push({
            course_id: courseId,
            module_id: getModuleIdFromCourseId(courseId, block.id),
            student_id: user.id,
            raw_possible: questionNumber,
            choices: Array(questionNumber)
              .fill(0)
              .map((_, idx) => {
                return {
                  id: `${block.id}_${idx + 2}_1`,
                  correctness: "",
                  student_answer: "",
                };
              }),
          });
        }
      }
      setNonGrades(nonGradeTmp);
    }
  }, [loadedCourseUnit, xblocks, currCourse, courseId, user.id]);

  // Very important function that submit student answers
  /**
   * Submits a problem answer payload for the current section.
   *
   * @param {Object} payload Answer payload.
   * @param {string} [postFlag="ALLOW_POST"] Whether to persist grades remotely.
   * @returns {void}
   */
  const onSubmitProblem = (payload, postFlag = "ALLOW_POST") => {
    let payloadList = [payload];

    // If there is non grades inside the same board, then mimic and send them as new answer
    payloadList = nonGrades.length ? [...payloadList, ...nonGrades.filter((g) => g.module_id !== payload.module_id)] : payloadList;
    setNonGrades([]);

    // If success, but retry mode, then we dont' send post request, and store the answers temp, and whenever the answer is correct, then post
    setStackedAnswers(postFlag === "NOT_ALLOW_POST" ? [...stackedAnswers, ...payloadList] : []);

    postFlag = preview ? false : postFlag;
    const value = {
      payload_list: payloadList,
      course_id: courseId,
      section_title: sectionTitle,
      post_flag: postFlag,
    };

    actionAnswerSubmit(dispatch, value);
  };

  /**
   * Re-submits every problem in the current unit.
   *
   * @returns {void}
   */
  const onReSubmitProblems = () => {
    const value = {
      payload_list: stackedAnswers,
      course_id: courseId,
      section_title: sectionTitle,
      post_flag: "ALLOW_POST",
    };
    actionAnswerSubmit(dispatch, value);
  };

  return (
    <Box className="w-full flex justify-center v-content">
      <Box className="max-w-5xl mx-8 my-16 w-full">
        <Box className="flex v-bg-body flex-col md:flex-row">
          <Box className="content w-full ml-auto mr-auto xblock-content v-bg-body position-relative">
            <Box id="model">
              <VText className="text-base" div color="secondary">
                {loadedCourseUnit ? moduleType !== MODULE_TYPE.MOCKFINAL ? sectionTitle : "" : <Skeleton width="100px" />}
              </VText>
              {moduleType !== MODULE_TYPE.MOCKFINAL && moduleType !== MODULE_TYPE.MOCKEXAM && moduleType !== MODULE_TYPE.CONCLUSION && (
                <VText className="text-base mt-2" div color="secondary">
                  {loadedCourseUnit ? sequenceTitle : <Skeleton width="150px" />}
                </VText>
              )}

              <VText className="mt-4 text-3xl font-bold " div>
                {loadedCourseUnit ? getVerticalTitle(structure, sectionIndex, sequenceIndex, verticalIndex) : <Skeleton width="50%" />}
              </VText>
              <Box className="mt-4 text-sm">
                {loadedCourseUnit ? (
                  <VText color="secondary">
                    {discussionId !== null ? comments.length || "0" : "0"}&nbsp;{t("Comments")}
                  </VText>
                ) : (
                  <Skeleton width="100px" />
                )}
              </Box>
            </Box>

            {loadedCourseUnit && moduleType === MODULE_TYPE.HTML && <ClassicModuleRender currCourse={currCourse} xblocks={xblocks} onSubmitProblem={onSubmitProblem} />}
            {loadedCourseUnit && moduleType === MODULE_TYPE.PRETEST && <TestPositionmentRender xblocks={xblocks} onSubmitProblem={onSubmitProblem} />}
            {loadedCourseUnit && moduleType === MODULE_TYPE.MOCKEXAM && <MockExamModuleRender xblocks={xblocks} onSubmitProblem={onSubmitProblem} onReSubmitProblems={onReSubmitProblems} setStackedAnswers={setStackedAnswers} />}
            {loadedCourseUnit && moduleType === MODULE_TYPE.CONCLUSION && <ConclusionRender xblocks={xblocks} onSubmitProblem={onSubmitProblem} />}
            {loadedCourseUnit && moduleType === MODULE_TYPE.MOCKFINAL && <FinalEvaluationRender xblocks={xblocks} onSubmitProblem={onSubmitProblem} onReSubmitProblems={onReSubmitProblems} setStackedAnswers={setStackedAnswers} />}
            {loadedCourseUnit && moduleType === MODULE_TYPE.HTML && <BoardReview />}
            {!loadedCourseUnit && <Skeleton count={10} className="mt-3" />}

            {/* <PrevNextNav prevLink={prevLink} nextLink={nextLink} courseId={courseId} enable={pageNavShow} /> */}

            {discussionId && !isPosted && (
              <>
                <Box className="mt-5">
                  <Box className="text-2xl font-bold mb-4 mt-8">
                    <VText>{t("Post a Comment on this course")}</VText>
                  </Box>
                  <CommentCard discussionId={discussionId} />
                </Box>
              </>
            )}
            <Box className="mt-8">
              {discussionId && loadedCommentList && comments.data?.length ? (
                <>
                  <VText className="font-bold text-xl mb-4" div>
                    Comments
                  </VText>
                  {(comments.data || []).map((comment) => (
                    <CourseCommentItem comment={comment} key={comment._id} />
                  ))}
                </>
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </Box>

        {/******************************* Pagination ********************************/}
        <BoardPagination />
      </Box>
    </Box>
  );
}

export default BoardContent;
